// Socket.IO singleton for the chat feature. The chat server shares the API host,
// so we derive it from REACT_APP_API_URL by stripping the trailing "/api".
//
// The JWT is passed WITHOUT the "Bearer " prefix (auth.token), per the backend.

import { io, Socket } from "socket.io-client";
import type {
  ChatChannel,
  ChatSocketAck,
  ChatSocketMessageEvent,
  ChatSocketEndedEvent,
  ChatSocketReadEvent,
  ChatSocketTypingEvent,
  ChatSocketDeletedEvent,
  ChatSocketMembersAddedEvent,
  ChatSocketMemberLeftEvent,
  ChatSocketTransferredEvent,
  ChatSocketMessagePinEvent,
  ChatSocketReactionEvent,
} from "../types/chat";

// e.g. "https://apidev.woopsa.app/api" -> "https://apidev.woopsa.app"
export const getChatSocketHost = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  return apiUrl.replace(/\/api\/?$/, "");
};

let socket: Socket | null = null;

// Returns a shared, connected socket for the current token. If the token
// changed since the last connect (re-login), the old socket is torn down.
export const getChatSocket = (): Socket => {
  const token = localStorage.getItem("token") || "";

  if (socket && (socket.auth as { token?: string })?.token !== token) {
    socket.disconnect();
    socket = null;
  }

  if (!socket) {
    socket = io(getChatSocketHost(), {
      auth: { token },
      transports: ["websocket"],
      autoConnect: true,
    });
  } else if (!socket.connected) {
    socket.connect();
  }

  return socket;
};

export const disconnectChatSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

// ---- Typed emit helpers (each takes an optional ack callback) ----

export const joinConversation = (
  channel: ChatChannel,
  conversationId: number,
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:join", { channel, conversationId }, ack);
};

export const leaveConversation = (
  channel: ChatChannel,
  conversationId: number
): void => {
  if (socket) socket.emit("chat:leave", { channel, conversationId });
};

export const sendSocketMessage = (
  payload: {
    channel: ChatChannel;
    conversationId?: number;
    customerNumber?: number;
    targetUserId?: number;
    content: string;
  },
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:send", payload, ack);
};

export const editSocketMessage = (
  channel: ChatChannel,
  messageId: number,
  content: string,
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:edit", { channel, messageId, content }, ack);
};

export const deleteSocketMessage = (
  channel: ChatChannel,
  messageId: number,
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:delete", { channel, messageId }, ack);
};

export const endSocketChat = (
  channel: ChatChannel,
  conversationId: number,
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:end", { channel, conversationId }, ack);
};

// Sales hands a retailer<->sales chat to another sales person. The server replies
// via the ack and broadcasts 'chat:transferred' to the old + new recipients.
export const transferSocketChat = (
  conversationId: number,
  targetUserId: number,
  note?: string | null,
  ack?: (res: ChatSocketAck) => void
): void => {
  getChatSocket().emit("chat:transfer", { conversationId, targetUserId, note: note ?? undefined }, ack);
};

export const emitRead = (channel: ChatChannel, conversationId: number): void => {
  getChatSocket().emit("chat:read", { channel, conversationId });
};

export const emitTyping = (channel: ChatChannel, conversationId: number): void => {
  getChatSocket().emit("chat:typing", { channel, conversationId });
};

// Re-export event payload types for convenience in consumers.
export type {
  ChatSocketMessageEvent,
  ChatSocketEndedEvent,
  ChatSocketReadEvent,
  ChatSocketTypingEvent,
  ChatSocketDeletedEvent,
  ChatSocketMembersAddedEvent,
  ChatSocketMemberLeftEvent,
  ChatSocketTransferredEvent,
  ChatSocketMessagePinEvent,
  ChatSocketReactionEvent,
};
